// @ts-nocheck - MUI 7.x Grid item prop type compatibility
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AccountBalance,
  People,
  Description,
  AttachMoney,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { biAnalyticsService } from '../services/biAnalytics';

interface KPIs {
  total_monthly_revenue: number;
  active_clients_count: number;
  outstanding_ar_amount: number;
  active_contracts_count: number;
  monthly_growth_rate: number;
}

interface RevenueTrend {
  month: string;
  revenue: number;
  client_count: number;
  invoice_count: number;
}

interface TopClient {
  client_id: string;
  client_name: string;
  total_revenue: number;
  invoice_count: number;
}

interface ARAging {
  aging_0_30: number;
  aging_31_60: number;
  aging_61_90: number;
  aging_over_90: number;
}

interface RevenueByType {
  month: string;
  bw_revenue: number;
  color_revenue: number;
}

interface VolumeTrend {
  month: string;
  bw_copies: number;
  color_copies: number;
  total_copies: number;
  color_ratio: number;
}

interface DashboardData {
  kpis: KPIs;
  revenue_trend: RevenueTrend[];
  top_clients: TopClient[];
  ar_aging: ARAging;
  revenue_by_type: RevenueByType[];
  volume_trend: VolumeTrend[];
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

export default function FinancialAnalytics() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [months, setMonths] = useState(12);

  useEffect(() => {
    loadDashboardData();
  }, [months]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await biAnalyticsService.getFinancialDashboard(months);
      setData(response);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al cargar datos del dashboard');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-PA', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('es-PA').format(num);
  };

  const formatMonth = (monthStr: string): string => {
    const [year, month] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('es-PA', { year: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (!data) {
    return (
      <>
        <Alert severity="error">
          No se pudieron cargar los datos del dashboard
        </Alert>
      </>
    );
  }

  // Prepare AR Aging data for pie chart
  const arAgingData = [
    { name: '0-30 días', value: data.ar_aging.aging_0_30 },
    { name: '31-60 días', value: data.ar_aging.aging_31_60 },
    { name: '61-90 días', value: data.ar_aging.aging_61_90 },
    { name: '90+ días', value: data.ar_aging.aging_over_90 },
  ].filter(item => item.value > 0);

  return (
    <>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Business Intelligence - Análisis Financiero
          </Typography>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Período</InputLabel>
            <Select
              value={months}
              label="Período"
              onChange={(e) => setMonths(e.target.value as number)}
            >
              <MenuItem value={6}>Últimos 6 meses</MenuItem>
              <MenuItem value={12}>Últimos 12 meses</MenuItem>
              <MenuItem value={18}>Últimos 18 meses</MenuItem>
              <MenuItem value={24}>Últimos 24 meses</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* KPI Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Ingresos del Mes
                    </Typography>
                    <Typography variant="h5" component="div">
                      {formatCurrency(data.kpis.total_monthly_revenue)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      {data.kpis.monthly_growth_rate >= 0 ? (
                        <TrendingUp sx={{ color: 'success.main', mr: 0.5 }} />
                      ) : (
                        <TrendingDown sx={{ color: 'error.main', mr: 0.5 }} />
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          color: data.kpis.monthly_growth_rate >= 0 ? 'success.main' : 'error.main'
                        }}
                      >
                        {data.kpis.monthly_growth_rate >= 0 ? '+' : ''}
                        {data.kpis.monthly_growth_rate.toFixed(1)}% vs mes anterior
                      </Typography>
                    </Box>
                  </Box>
                  <AttachMoney sx={{ fontSize: 50, color: 'primary.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Clientes Activos
                    </Typography>
                    <Typography variant="h5" component="div">
                      {formatNumber(data.kpis.active_clients_count)}
                    </Typography>
                  </Box>
                  <People sx={{ fontSize: 50, color: 'success.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Cuentas por Cobrar
                    </Typography>
                    <Typography variant="h5" component="div">
                      {formatCurrency(data.kpis.outstanding_ar_amount)}
                    </Typography>
                  </Box>
                  <AccountBalance sx={{ fontSize: 50, color: 'warning.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Contratos Activos
                    </Typography>
                    <Typography variant="h5" component="div">
                      {formatNumber(data.kpis.active_contracts_count)}
                    </Typography>
                  </Box>
                  <Description sx={{ fontSize: 50, color: 'info.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Revenue Trend Chart */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} lg={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Tendencia de Ingresos
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={data.revenue_trend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="month"
                      tickFormatter={formatMonth}
                    />
                    <YAxis
                      tickFormatter={(value: number) => `$${(value / 1000).toFixed(0)}k`}
                    />
                    <Tooltip
                      formatter={(value: number) => formatCurrency(value)}
                      labelFormatter={formatMonth}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      name="Ingresos"
                      stroke="#8884d8"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* AR Aging Pie Chart */}
          <Grid item xs={12} lg={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Antigüedad de Cuentas por Cobrar
                </Typography>
                {arAgingData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={arAgingData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry: any) => `${entry.name}: ${formatCurrency(entry.value as number)}`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {arAgingData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                    <Typography variant="body2" color="textSecondary">
                      No hay cuentas por cobrar pendientes
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Revenue by Type Stacked Bar Chart */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Ingresos por Tipo de Copia (B/N vs Color)
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.revenue_by_type}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tickFormatter={formatMonth} />
                    <YAxis tickFormatter={(value: number) => `$${(value / 1000).toFixed(0)}k`} />
                    <Tooltip
                      formatter={(value: number) => formatCurrency(value)}
                      labelFormatter={formatMonth}
                    />
                    <Legend />
                    <Bar dataKey="bw_revenue" name="B/N" stackId="a" fill="#8884d8" />
                    <Bar dataKey="color_revenue" name="Color" stackId="a" fill="#82ca9d" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Top 10 Clients Table */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Top 10 Clientes por Ingresos ({months === 6 ? 'Últimos 6 meses' : months === 12 ? 'Últimos 12 meses' : months === 18 ? 'Últimos 18 meses' : 'Últimos 24 meses'})
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Posición</TableCell>
                        <TableCell>ID Cliente</TableCell>
                        <TableCell>Nombre</TableCell>
                        <TableCell align="right">Facturas</TableCell>
                        <TableCell align="right">Ingresos Totales</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.top_clients.map((client, index) => (
                        <TableRow key={client.client_id} hover>
                          <TableCell>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                bgcolor: index < 3 ? 'primary.main' : 'grey.300',
                                color: index < 3 ? 'white' : 'text.primary',
                                fontWeight: 'bold',
                              }}
                            >
                              {index + 1}
                            </Box>
                          </TableCell>
                          <TableCell>{client.client_id}</TableCell>
                          <TableCell>{client.client_name}</TableCell>
                          <TableCell align="right">{client.invoice_count}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(client.total_revenue)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}
